import { useState, useEffect } from 'react';
import { AttributeFacet, BasicFacet, BasicFacetValue } from "../types";
import { RangeSlider } from "./RangeSlider";
import { SingleValueSlider } from "./SingleValueSlider";

interface AttributeFacetComponentProps {
  facet: AttributeFacet;
  facetKey: string;
  isExpanded: boolean;
  selectedFilters: Record<string, Set<string>>;
  onFilterChange: (
    facetKey: string,
    value: BasicFacetValue,
    checked: boolean,
    type: string | null,
    isExtra: boolean
  ) => void;
  sanitizeFilterValue: (value: string) => string;
  isFilterSelected: (
    facet: BasicFacet | AttributeFacet,
    value: BasicFacetValue
  ) => boolean;
}

export const AttributeFacetComponent: React.FC<
  AttributeFacetComponentProps
> = ({
  facet,
  facetKey,
  isExpanded,
  onFilterChange,
  sanitizeFilterValue,
  isFilterSelected,
}) => {
  const [sliderRange, setSliderRange] = useState<[number, number]>([0, 100]);
  // const [isExtraAttribute, setIsExtraAttribute] = useState<boolean>(false);

  facet.values = facet.values.sort((a, b) => a.filter.localeCompare(b.filter));
  // Initialize slider values from facet data
  useEffect(() => {
    if (facet.isNumber && facet.values.length > 0) {
      const numericValues = facet.values
        .map((item) => parseFloat(item.filter))
        .filter((value) => !isNaN(value));

      if (numericValues.length > 0) {
        const min = Math.min(...numericValues);
        const max = Math.max(...numericValues);
        setSliderRange([min, max]);
      }
    }
    // setIsExtraAttribute(facet.isExtra);
  }, [facet]);

  // Handle range slider change
  const handleRangeSliderChange = (min: number, max: number) => {
    setSliderRange([min, max]);

    // Create a synthetic BasicFacetValue for the range
    const rangeValue: BasicFacetValue = {
      count: 0,
      filter: `${min}-${max}`,
      indexValue: `${min}-${max}`,
    };

    // Call the parent's filter change handler
    onFilterChange(facetKey, rangeValue, true, facet.type, facet.isExtra);
  };

  // Handle single value slider change
  const handleSingleValueSliderChange = (value: number) => {
    // setSingleValue(value);

    // Create a synthetic BasicFacetValue for the single value
    const singleValue: BasicFacetValue = {
      count: 0,
      filter: `${value}`,
      indexValue: `${value}`,
    };
    if (value === -1) {
      onFilterChange(facetKey, singleValue, false, facet.type, facet.isExtra);
      return;
    }
    // Call the parent's filter change handler
    onFilterChange(facetKey, singleValue, true, facet.type, facet.isExtra);
  };

  if (!isExpanded) {
    return null;
  }

  // If this is a numeric facet, render the appropriate slider
  if (facet.isNumber) {
    return (
      <div className="space-y-1">
        <label className="text-sm text-gray-700">{facet.label}</label>
        {facet.range ? (
          <RangeSlider
            min={sliderRange[0]}
            max={sliderRange[1]}
            initialMin={sliderRange[0]}
            initialMax={sliderRange[1]}
            suffix={facet.suffix}
            onChange={handleRangeSliderChange}
          />
        ) : (
          <SingleValueSlider
            facet={facet as BasicFacet}
            suffix={facet.suffix}
            onFilterChange={handleSingleValueSliderChange}
            isExpanded={isExpanded}
            isFilterSelected={isFilterSelected}
          />
        )}
      </div>
    );
  }

  // For non-numeric attribute facets, render checkboxes
  return (
    <div className="ml-2 space-y-1">
      {facet.values.map((item: BasicFacetValue) => {
        const sanitizedFilter = sanitizeFilterValue(item.filter);
        return (
          <div key={item.filter} className="flex items-top">
            <input
              type="checkbox"
              id={`${facetKey}-${sanitizedFilter}`}
              checked={isFilterSelected(facet, item)}
              onChange={(e) =>
                onFilterChange(facetKey, item, e.target.checked, facet.type, facet.isExtra)
              }
              className="mr-2 h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            />
            <label
              htmlFor={`${facetKey}-${sanitizedFilter}`}
              className="text-sm text-gray-700 cursor-pointer flex items-top justify-between w-full"
            >
              <span>{sanitizedFilter}</span>
              <span className="text-xs text-gray-500 ml-1">({item.count})</span>
            </label>
          </div>
        );
      })}
    </div>
  );
};
